import { decorate, injectable } from '@evertel/di'

export type GPCPushData = {
    type: 'RoomMessage' | 'ThreadMessage' | 'Room_updated' | 'AuditComplete'
    isUrgent?: boolean
    departmentId?: number
    id?: number
    roomId?: number
    threadId?: number
    ownerId?: number
    blueUserId?: number
    action?: 'room_join_request_accepted' | 'room_join_request_created' | 'member_added' | 'member_removed' | 'manager_added' | 'manager_removed'
    repliesToId?: number
    auditType?: string
}
export interface GPCPushNotification {
    notification?: {
        id?: string
        sound?: string
        icon?: string
        image?: string  // Notification image
        tag?: string
        title?: string
        body?: string
        badge?: number
        // Android specific fields
        android?: {
            channel_id?: string  // Android notification channel
            priority?: 'default' | 'high' | 'max'  // Notification priority
            auto_cancel?: boolean  // Auto dismiss notification
        }
        // iOS specific fields
        ios?: {
            critical?: boolean  // iOS critical alert
            criticalVolume?: number  // iOS critical alert volume (0-1)
            attachments?: Array<{ url: string }>  // iOS attachments
        }
    }
    data: GPCPushData
}

export type NotificationPermissionResult = Promise<NotificationPermission>

export type NotificationPermission = 'default' | 'denied' | 'granted' | undefined

export interface ScheduleNotificationOptions {
    title: string
    body: string
    fireDate: number | string // timestamp in milliseconds
    type: 'room' | 'thread' | 'idle'
    isUrgent?: boolean
    sound?: string
    duration?: string // for UI messages
    payload?: any
    image?: string
}

abstract class PushImplementation {
    public onMessageCallback: ((notification: GPCPushNotification) => void) | undefined
    public onPermissionChangeCallback: ((permission: NotificationPermission) => void) | undefined // This is called by the PushImplementation SubClass (not unused !)
    public onNotificationClickedCallback: ((notification: GPCPushNotification) => void) | undefined
    public pendingNotification: GPCPushNotification | undefined

    constructor() {
        throw new Error('Do not use directly, push implementation must be platform specific! Create a platform specific implementation with the same shape of this class and inject it.')
    }

    async init() {
        throw new Error('Not Implemented')
    }

    async promptForNotificationPermission(): NotificationPermissionResult {
        throw new Error('Not Implemented')
    }

    async getCurrentNotificationPermission(): NotificationPermissionResult {
        throw new Error('Not Implemented')
    }

    async getPushToken(): Promise<string> {
        throw new Error('Not Implemented')
    }

    async deletePushToken() {
        throw new Error('Not Implemented')
    }

    processPendingNotification() {
        throw new Error('Not Implemented')
    }

    async showLocalNotification() {
        throw new Error('Not Implemented')
    }

    async scheduleNotification(options: ScheduleNotificationOptions): Promise<string | undefined> {
        throw new Error('Not Implemented')
    }

    async setBadge?(count: number): Promise<void> {
        throw new Error('Not Implemented')
    }

    async getDisplayedNotifications(): Promise<GPCPushNotification[]> {
        throw new Error('Not Implemented')
    }

    async cancelNotifications(notificationIds: string[]): Promise<boolean> {
        throw new Error('Not Implemented')
    }
}

decorate(injectable(), PushImplementation)

export { PushImplementation }
